import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import type { LucideIcon } from "lucide-react";
import {
  ArrowRight,
  BadgePercent,
  CalendarCheck,
  CalendarClock,
  Check,
  Download,
  Handshake,
  HardDrive,
  Layers,
  MonitorUp,
  ReceiptText,
  Repeat,
  Scale,
  Server,
  ShieldCheck,
  Sparkles,
  TrendingUp,
  UploadCloud,
  Users,
} from "lucide-react";
import AppHeader from "@/components/AppHeader";
import Footer from "@/components/Footer";
import CountryExamples from "@/components/pricing2026/CountryExamples";
import PlanMove from "@/components/pricing2026/PlanMove";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ON_DEMAND_FTP_PATH } from "@/content/onDemandFtp";
import { BIOMETRIC_POLICY_PATH, CHANGES_PUBLIC, PRICING_PATHS, TODAY_PLANS, newPlan } from "@/content/pricing/plans";
import { fill, formatCents, formatCount, formatStorage, formatUsd, usePricingCopy } from "@/content/pricing";
import { ratePercent } from "@/lib/pricing/orderFee";

// "Pricing is changing on 1 November 2026" at /pricing-changes-2026: why, when and the comparison,
// with localised country examples. Public (indexable, linked from the announcement email) from
// 20 October 2026: CHANGES_PUBLIC in plans.ts; set it to false to put noindex,nofollow back (and
// restore the X-Robots-Tag rule in server.mjs). Not in the nav / footer. Copy:
// src/content/pricing/copy.<lang>.ts.
// Numbers: src/content/pricing/plans.ts and countries.ts.

const WHY_ICONS: Record<string, LucideIcon> = {
  rates: BadgePercent,
  storage: HardDrive,
  downloads: Download,
  simple: Layers,
  order: ReceiptText,
  deals: Handshake,
};

const WHEN_ICONS: Record<string, LucideIcon> = {
  action: CalendarCheck,
  month: CalendarClock,
  change: Repeat,
  deals: Handshake,
  privacy: ShieldCheck,
  team: Users,
};

const FTP_ICONS: Record<string, LucideIcon> = {
  ondemand: Server,
  web: UploadCloud,
  studio: MonitorUp,
};

type Direction = "better" | "worse" | "neutral";

/** One "today → new" row in a comparison card. */
const CompareRow = ({
  label,
  today,
  next,
  dir,
  srToday,
  srNext,
}: {
  label: string;
  today: ReactNode;
  next: ReactNode;
  dir: Direction;
  /** Screen-reader labels for the two values ("Today", "From 1 November"). */
  srToday: string;
  srNext: string;
}) => (
  <div className="py-3">
    <dt className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</dt>
    <dd className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-sm">
      <span className="sr-only">{srToday}: </span>
      <span className="text-muted-foreground line-through decoration-muted-foreground/50">{today}</span>
      <ArrowRight size={13} className="self-center text-hawk-orange" aria-hidden="true" />
      <span className="sr-only">{srNext}: </span>
      <span
        className={`font-semibold ${
          dir === "better" ? "text-emerald-700 dark:text-emerald-400" : dir === "worse" ? "text-amber-700 dark:text-amber-400" : ""
        }`}
      >
        {next}
      </span>
    </dd>
  </div>
);

const PricingChanges2026 = () => {
  const loc = usePricingCopy();
  const { copy, numberLocale } = loc;
  const c = copy.changes;
  const usd = (n: number) => formatUsd(n, numberLocale);
  const pct = (r: number) => ratePercent(r, numberLocale);
  const storage = (gb: number) => formatStorage(gb, copy, numberLocale);

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{c.seo.title}</title>
        <meta name="description" content={c.seo.description} />
        {!CHANGES_PUBLIC && <meta name="robots" content="noindex, nofollow" />}
      </Helmet>
      <AppHeader variant="marketing" />

      <main>
        {/* Hero */}
        <section className="relative overflow-hidden bg-hawk-navy pb-16 pt-32 text-white">
          <div className="absolute inset-0 bg-radial-orange opacity-20" />
          <div className="container relative z-10 mx-auto px-4">
            <div className="mx-auto max-w-3xl text-center">
              <span className="mb-4 inline-flex items-center gap-2 rounded-full bg-hawk-orange/15 px-4 py-1.5 text-sm font-semibold text-hawk-orange">
                <CalendarClock size={14} />
                {c.hero.badge}
              </span>
              <h1 className="mb-6 text-4xl font-display font-bold md:text-5xl">
                {c.hero.title} <span className="text-gradient-primary">{c.hero.titleHighlight}</span>
              </h1>
              <p className="mx-auto mb-8 max-w-2xl text-lg text-white/80">{c.hero.subtitle}</p>
              <div className="flex flex-wrap justify-center gap-3">
                <Button variant="hero" size="lg" asChild>
                  <Link to={PRICING_PATHS.pricing}>
                    {c.hero.ctaPrimary}
                    <ArrowRight />
                  </Link>
                </Button>
                <Button variant="heroOutline" size="lg" asChild>
                  <a href="#compare">{c.hero.ctaSecondary}</a>
                </Button>
              </div>
            </div>

            {/* Every rate comes down */}
            <ul className="mx-auto mt-12 grid max-w-4xl grid-cols-2 gap-3 md:grid-cols-4">
              {TODAY_PLANS.map((from) => {
                const to = newPlan(from.movesTo);
                return (
                  <li key={from.id} className="rounded-2xl border border-white/15 bg-white/5 p-4 text-center backdrop-blur">
                    <p className="mb-2 text-sm font-semibold text-white/80">
                      <PlanMove from={from} copy={copy} arrowSize={12} />
                    </p>
                    <p className="text-lg text-white/60">
                      <s>{pct(from.commissionRate)}</s>
                    </p>
                    <p className="text-4xl font-display font-bold">{pct(to.commissionRate)}</p>
                  </li>
                );
              })}
            </ul>
          </div>
        </section>

        {/* Why */}
        <section className="py-20" aria-labelledby="why-heading">
          <div className="container mx-auto px-4">
            <h2 id="why-heading" className="mx-auto mb-12 max-w-2xl text-center text-3xl font-display font-bold md:text-4xl">
              {c.why.heading}
            </h2>
            <ul className="mx-auto grid max-w-6xl gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {c.why.items.map((item) => {
                const Icon = WHY_ICONS[item.id] ?? Check;
                return (
                  <li key={item.id} className="rounded-2xl bg-card p-6 shadow-lg">
                    <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-hawk-orange to-hawk-gold">
                      <Icon className="text-white" size={22} />
                    </div>
                    <h3 className="mb-1 font-display text-lg font-bold">{item.title}</h3>
                    <p className="text-sm text-muted-foreground">{item.body}</p>
                  </li>
                );
              })}
            </ul>
            <div className="mx-auto mt-8 flex max-w-6xl gap-4 rounded-2xl border border-hawk-gold/50 bg-hawk-gold/5 p-6">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-hawk-gold/15">
                <Scale size={18} className="text-hawk-orange" />
              </div>
              <div>
                <h3 className="mb-1 font-display text-lg font-bold">{c.why.honestHeading}</h3>
                <p className="text-sm text-muted-foreground">{c.why.honestBody}</p>
              </div>
            </div>
          </div>
        </section>

        {/* When / what happens */}
        <section className="bg-secondary/30 py-20" aria-labelledby="when-heading">
          <div className="container mx-auto px-4">
            <div className="mx-auto mb-12 max-w-2xl text-center">
              <span className="mb-4 inline-flex items-center gap-2 rounded-full bg-hawk-orange/10 px-4 py-1.5 text-sm font-semibold text-hawk-orange">
                <CalendarCheck size={14} />
                {copy.common.changeDate}
              </span>
              <h2 id="when-heading" className="mb-3 text-3xl font-display font-bold md:text-4xl">
                {c.when.heading}
              </h2>
              <p className="text-muted-foreground">{c.when.subtitle}</p>
            </div>
            <div className="mx-auto grid max-w-6xl gap-8 lg:grid-cols-[1.2fr_1fr]">
              <ul className="divide-y divide-border rounded-2xl bg-card px-6 shadow-lg">
                {TODAY_PLANS.map((from) => (
                  <li key={from.id} className="py-5">
                    <p className="mb-1 font-display text-lg font-bold">
                      <PlanMove from={from} copy={copy} arrowSize={16} />
                    </p>
                    <p className="text-sm text-muted-foreground">{c.when.moves[from.id]}</p>
                  </li>
                ))}
              </ul>
              <ul className="space-y-4">
                {c.when.points.map((point) => {
                  const Icon = WHEN_ICONS[point.id] ?? Check;
                  return (
                    <li key={point.id} className="flex gap-4 rounded-2xl border border-border bg-card p-5">
                      <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-hawk-orange/10">
                        <Icon size={18} className="text-hawk-orange" />
                      </div>
                      <div>
                        <h3 className="mb-1 font-semibold">{point.title}</h3>
                        <p className="text-sm text-muted-foreground">{point.body}</p>
                        {point.id === "privacy" && BIOMETRIC_POLICY_PATH && (
                          <Link
                            to={BIOMETRIC_POLICY_PATH}
                            className="mt-1 inline-flex items-center gap-1 text-sm font-medium text-hawk-orange underline underline-offset-2"
                          >
                            {c.privacyLink}
                            <ArrowRight size={14} />
                          </Link>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </section>

        {/* The web uploader is the standard way to upload; shared FTP closes on 1 November 2026; on-demand FTP is the fallback */}
        <section className="bg-secondary/30 pb-20" aria-labelledby="ftp-heading">
          <div className="container mx-auto px-4">
            <div className="mx-auto max-w-6xl rounded-2xl border border-hawk-gold/50 bg-card p-6 shadow-lg md:p-8">
              <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <h2 id="ftp-heading" className="mb-1 font-display text-2xl font-bold md:text-3xl">
                    {c.ftp.heading}
                  </h2>
                  <p className="text-muted-foreground">{c.ftp.body}</p>
                </div>
                <span className="inline-flex flex-shrink-0 items-center gap-2 self-start rounded-full bg-hawk-orange/10 px-4 py-1.5 text-sm font-semibold text-hawk-orange">
                  <CalendarClock size={14} />
                  {c.ftp.date}
                </span>
              </div>
              <ul className="grid gap-4 md:grid-cols-3">
                {c.ftp.items.map((item) => {
                  const Icon = FTP_ICONS[item.id] ?? Check;
                  return (
                    <li key={item.id} className="flex gap-4 rounded-xl border border-border p-4">
                      <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-hawk-orange/10">
                        <Icon size={18} className="text-hawk-orange" />
                      </div>
                      <div>
                        <h3 className="mb-1 font-semibold">{item.title}</h3>
                        <p className="text-sm text-muted-foreground">{item.body}</p>
                      </div>
                    </li>
                  );
                })}
              </ul>
              <Link to={ON_DEMAND_FTP_PATH} className="mt-6 inline-flex items-center gap-1.5 font-semibold text-hawk-orange hover:underline">
                {copy.common.onDemandFtpLink}
                <ArrowRight size={15} aria-hidden="true" />
              </Link>
            </div>
          </div>
        </section>

        {/* What's included: a short summary, linking to "Compare every feature" on the pricing page */}
        <section id="whats-included" className="scroll-mt-24 pt-20" aria-labelledby="included-heading">
          <div className="container mx-auto px-4">
            <div className="mx-auto flex max-w-6xl flex-col gap-4 rounded-2xl bg-card p-6 shadow-lg md:flex-row md:items-center md:justify-between md:gap-10 md:p-8">
              <div>
                <h2 id="included-heading" className="mb-2 font-display text-2xl font-bold md:text-3xl">
                  {c.included.heading}
                </h2>
                <p className="text-muted-foreground">{c.included.everyPlan}</p>
                <p className="mt-1 text-muted-foreground">{c.included.upgrades}</p>
              </div>
              <Link
                to={`${PRICING_PATHS.pricing}#compare-features`}
                className="inline-flex flex-shrink-0 items-center gap-1.5 self-start font-semibold text-hawk-orange hover:underline md:self-center"
              >
                {copy.pricing.compare.linkLabel}
                <ArrowRight size={15} aria-hidden="true" />
              </Link>
            </div>
          </div>
        </section>

        {/* Comparison */}
        <section id="compare" className="scroll-mt-24 py-20" aria-labelledby="compare-heading">
          <div className="container mx-auto px-4">
            <div className="mx-auto mb-12 max-w-2xl text-center">
              <h2 id="compare-heading" className="mb-3 text-3xl font-display font-bold md:text-4xl">
                {c.compare.heading}
              </h2>
              <p className="text-muted-foreground">{c.compare.subtitle}</p>
            </div>
            <ul className="mx-auto grid max-w-6xl gap-6 md:grid-cols-2 lg:grid-cols-4">
              {TODAY_PLANS.map((from) => {
                const to = newPlan(from.movesTo);
                const r = c.compare.rows;
                const dir = (better: boolean, same: boolean): Direction => (same ? "neutral" : better ? "better" : "worse");
                return (
                  <li key={from.id} className="flex flex-col rounded-2xl bg-card p-6 shadow-lg">
                    <h3 className="mb-1 font-display text-xl font-bold">
                      <PlanMove from={from} copy={copy} arrowSize={16} />
                    </h3>
                    <p className="mb-2 text-xs text-muted-foreground">
                      {copy.common.today} <ArrowRight size={11} className="inline" aria-hidden="true" /> {copy.common.fromChangeDate}
                    </p>
                    <dl className="divide-y divide-border">
                      <CompareRow
                        srToday={copy.common.today}
                        srNext={copy.common.fromChangeDate}
                        label={r.fee}
                        today={`${usd(from.priceUsd)}${copy.common.perMonth}`}
                        next={`${usd(to.priceUsd)}${copy.common.perMonth}`}
                        dir={dir(to.priceUsd < from.priceUsd, to.priceUsd === from.priceUsd)}
                      />
                      <CompareRow
                        srToday={copy.common.today}
                        srNext={copy.common.fromChangeDate}
                        label={r.commission}
                        today={pct(from.commissionRate)}
                        next={pct(to.commissionRate)}
                        dir={dir(to.commissionRate < from.commissionRate, to.commissionRate === from.commissionRate)}
                      />
                      <CompareRow
                        srToday={copy.common.today}
                        srNext={copy.common.fromChangeDate}
                        label={r.storage}
                        today={storage(from.storageGb)}
                        next={storage(to.storageGb)}
                        dir={dir(to.storageGb > from.storageGb, to.storageGb === from.storageGb)}
                      />
                      <CompareRow
                        srToday={copy.common.today}
                        srNext={copy.common.fromChangeDate}
                        label={r.downloads}
                        today={fill(c.compare.downloadsToday, { price: formatCents(from.freePhotoUsd, numberLocale) })}
                        next={fill(c.compare.downloadsNext, { count: formatCount(to.freeDownloads, numberLocale) })}
                        dir="better"
                      />
                      <CompareRow
                        srToday={copy.common.today}
                        srNext={copy.common.fromChangeDate}
                        label={r.extraStorage}
                        today={c.compare.extraStorageToday}
                        next={c.compare.extraStorageNext}
                        dir="neutral"
                      />
                    </dl>
                  </li>
                );
              })}
            </ul>
          </div>
        </section>

        {/* Country examples */}
        <section className="bg-secondary/30 py-20" aria-labelledby="countries-heading">
          <div className="container mx-auto px-4">
            <CountryExamples loc={loc} />
          </div>
        </section>

        {/* FAQ */}
        <section className="py-20" aria-labelledby="faq-heading">
          <div className="container mx-auto max-w-3xl px-4">
            <h2 id="faq-heading" className="mb-8 text-center text-3xl font-display font-bold md:text-4xl">
              {c.faq.heading}
            </h2>
            <Accordion type="single" collapsible className="rounded-2xl bg-card px-6 shadow-lg">
              {c.faq.items.map((item, i) => (
                <AccordionItem key={item.q} value={`faq-${i}`} className={i === c.faq.items.length - 1 ? "border-b-0" : undefined}>
                  <AccordionTrigger className="text-left text-base">{item.q}</AccordionTrigger>
                  <AccordionContent forceMount className="text-muted-foreground">
                    <p>{item.a}</p>
                    {"id" in item && item.id === "ftp" && (
                      <Link to={ON_DEMAND_FTP_PATH} className="mt-3 inline-flex items-center gap-1.5 font-semibold text-hawk-orange hover:underline">
                        {copy.common.onDemandFtpLink}
                        <ArrowRight size={14} aria-hidden="true" />
                      </Link>
                    )}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* CTA */}
        <section className="relative overflow-hidden bg-hawk-navy py-20 text-white">
          <div className="absolute inset-0 bg-radial-orange opacity-20" />
          <div className="container relative z-10 mx-auto max-w-3xl px-4 text-center">
            <TrendingUp className="mx-auto mb-4 text-hawk-orange" size={32} />
            <h2 className="mb-3 text-3xl font-display font-bold md:text-4xl">{c.cta.heading}</h2>
            <p className="mb-8 text-white/80">{c.cta.body}</p>
            <Button variant="hero" size="lg" asChild>
              <Link to={PRICING_PATHS.pricing}>
                <Sparkles />
                {c.cta.button}
              </Link>
            </Button>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default PricingChanges2026;
